'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Button } from '../ui/button';
import { FaFacebook } from 'react-icons/fa';

import { RiLinksFill } from 'react-icons/ri';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { Heading } from '../ui/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

/**
 * The blog API returns neither `url` nor `canonical_url`, so the old `/blog/<slug>` fallback was
 * what got copied and shared — and it 404s: posts live at `ROUTES.BLOG_DETAIL` (`/<slug>`).
 */
function getBlogUrl(blog: Blog) {
  const explicit = (blog as any)?.canonical_url || (blog as any)?.url;
  return (
    explicit || new URL(ROUTES.BLOG_DETAIL({ slug: blog.slug }), window.location.origin).toString()
  );
}

export function BlogShare({ blog }: { blog: Blog }) {
  const { t } = useTranslations();

  const handleCopyLink = () => {
    if (typeof window === 'undefined') return;

    const baseUrl = getBlogUrl(blog);

    // `writeText` rejects asynchronously (permissions, insecure context); a sync try/catch never
    // saw that rejection.
    navigator.clipboard
      .writeText(baseUrl)
      .then(() => toast({ title: t('text3835'), variant: 'success' }))
      .catch(() => toast({ title: t('text4070'), variant: 'destructive' }));
  };

  const handleShare = () => {
    if (typeof window === 'undefined') return;

    const baseUrl = getBlogUrl(blog);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(baseUrl)}`;

    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  return (
    <div className="flex w-full max-w-[926px] flex-col items-center justify-center gap-2">
      <Heading title={t('text1155')} tag="h6" as="h2" center className={{ title: 'text-[#666666]' }} />
      <div className="flex w-full items-center justify-center gap-2">
        <Button
          variant={'none'}
          onClick={handleCopyLink}
          aria-label={t('text727')}
          className="max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] rounded-[4px] bg-walnut-soft !p-0 hover:bg-walnut-soft/80"
        >
          <RiLinksFill size={20} className="text-walnut" aria-hidden />
        </Button>
        <Button
          variant={'none'}
          onClick={handleShare}
          aria-label={t('text1047')}
          className="max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] rounded-[4px] bg-walnut-soft !p-0 hover:bg-walnut-soft/80"
        >
          <FaFacebook size={20} className="text-walnut" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
